// src/menu-items/dashboard.jsx

// assets
import { DashboardOutlined, BarChartOutlined } from '@ant-design/icons';
import CakeIcon from '@mui/icons-material/Cake';
import LiquorIcon from '@mui/icons-material/Liquor';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AssignmentAddIcon from '@mui/icons-material/AssignmentAdd';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import { ROLES, ACCESS_FLAGS } from '../lib/auth/roles';

// icons
const icons = {
  DashboardOutlined,
  BarChartOutlined
};

// Optional: export the ID so you can reference it elsewhere if needed
export const SPECIAL_EVENTS_MENU_ID = 'clubastra-specialEvents';

// ==============================|| MENU ITEMS - DASHBOARD ||============================== //

const dashboard = {
  id: 'group-dashboard',
  title: 'Navigation',
  type: 'group',
  children: [
    {
      id: 'dashboard',
      title: 'Dashboard',
      type: 'item',
      url: '/dashboard/default',
      icon: icons.DashboardOutlined,
      breadcrumbs: false,
      allowedRoles: [
        ROLES.DJ,
        ROLES.AMBASSADOR,
        ROLES.HOST,
        ROLES.HEAD_HOST,
        ROLES.MANAGEMENT,
        ROLES.ADMIN,
        ROLES.EMERSON,
        ROLES.YOURS_TRULY,
        ROLES.BAR_BAMBI
      ]
    },

    {
      id: 'clubastra-freeentry',
      title: 'Add Free Entries',
      type: 'item',
      url: '/freeentry',
      icon: LocalActivityIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.ADMIN]
    },

    {
      id: 'clubastra-addGuestlist',
      title: 'Add Guestlist',
      type: 'item',
      url: '/addGuestlist',
      icon: AssignmentAddIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    },
    // 🔑 Special Events – controlled by Firestore flag meta/uiSettings.specialEventsMenuEnabled
    {
      id: SPECIAL_EVENTS_MENU_ID,
      title: 'Special Events',
      type: 'item',
      url: '/specialEvents',
      icon: CelebrationIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN],
      // This key tells the sidebar that this item is behind a feature flag
      featureKey: 'specialEventsMenuEnabled'
    },

    {
      id: 'clubastra-managementfreelist',
      title: 'Add Management Frees',
      type: 'item',
      url: '/managementFreeList',
      icon: GroupAddIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.MANAGEMENT, ROLES.ADMIN],
      // Also visible to any user whose `users` doc has canAccessManagementFrees: true,
      // regardless of role (e.g. a trusted ambassador) — no management promotion required.
      allowedProfileFlags: [ACCESS_FLAGS.MANAGEMENT_FREE_LIST]
    },

    {
      id: 'clubastra-bday',
      title: 'Add Birthdays',
      type: 'item',
      url: '/bday',
      icon: CakeIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.DJ, ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    },

    {
      id: 'clubastra-booths',
      title: 'Add Booth Booking',
      type: 'item',
      url: '/booths',
      icon: LiquorIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.DJ, ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    },

    {
      id: 'clubastra-dinner',
      title: 'Add Dinner Booking',
      type: 'item',
      url: '/addDinner',
      icon: RestaurantIcon,
      breadcrumbs: false,
      hideForManagementAdminMenu: true,
      allowedRoles: [ROLES.DJ, ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.ADMIN]
    },
    {
      id: 'clubastra-dinner-management',
      title: 'Add Dinner Booking',
      type: 'item',
      url: '/addDinnerManagement',
      icon: RestaurantIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.MANAGEMENT, ROLES.ADMIN]
    },

    {
      id: 'clubastra-request-host',
      title: 'Request New Host',
      type: 'item',
      url: '/requestHost',
      icon: PersonAddIcon,
      breadcrumbs: false,
      hideForManagementAdminMenu: true,
      allowedRoles: [ROLES.ADMIN, ROLES.HEAD_HOST, ROLES.AMBASSADOR, ROLES.HOST]
    },

    {
      id: 'clubastra-management-request-host',
      title: 'Request New Host',
      type: 'item',
      url: '/managementRequestHost',
      icon: PersonAddIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.ADMIN, ROLES.MANAGEMENT]
    },

    {
      id: 'clubastra-tickets',
      title: 'Promote & Earn',
      type: 'item',
      url: '/tickets',
      icon: ConfirmationNumberIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.DJ, ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    },

    {
      id: 'clubastra-content',
      title: 'Host Promo Content',
      type: 'item',
      url: '/content',
      icon: PermMediaIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.DJ, ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    },

    {
      id: 'clubastra-perks',
      title: 'Host Benefits',
      type: 'item',
      url: '/perks',
      icon: CardGiftcardIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    },

    {
      id: 'clubastra-pays',
      title: 'Host Payments',
      type: 'item',
      url: '/pays',
      icon: LocalAtmIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    },

    {
      // Host Playbook — in-app training guides (registry self-gates per
      // brand/role/config, so the page always has content for whoever can
      // see this item). Hideable per brand via meta/uiSettings
      // disabledMenuItemIds like any other item — no new mechanism.
      id: 'clubastra-playbook',
      title: 'Host Playbook',
      type: 'item',
      url: '/playbook',
      icon: SchoolIcon,
      breadcrumbs: false,
      allowedRoles: [ROLES.DJ, ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    },

    {
      id: 'clubastra-missions',
      title: 'Missions',
      type: 'item',
      url: '/missions',
      icon: EmojiEventsIcon,
      breadcrumbs: false,
      // Config-gated nav (owner directive 2026-07-05): brands without the
      // missions program must see ZERO trace of it — no dead menu stub. The
      // flag is injected from gamification/config missions.enabled in
      // Navigation/index.jsx (same featureKey mechanism as Special Events).
      // Admins keep the /admin/incentives entry regardless — that's where the
      // program gets turned ON.
      featureKey: 'missionsMenuEnabled',
      allowedRoles: [ROLES.AMBASSADOR, ROLES.HOST, ROLES.HEAD_HOST, ROLES.MANAGEMENT, ROLES.ADMIN]
    }
  ]
};

export default dashboard;
